import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  downloadSerenityConfig,
  fetchSerenityConfigs,
} from "../../../api/serenityConfig";
import { SerenityConfigSummary } from "../../../generated/types";
import { TitleSection } from "../../common/TitleSection";
import { SerenityConfigCompareModal } from "./SerenityConfigCompareModal";

type ConfigsState = {
  configs: SerenityConfigSummary[];
  isLoading: boolean;
  failed: boolean;
};

/** Sauvegardes de config d'une Serenity, rechargées quand l'uuid change. */
export const useSerenityConfigs = (uuid: string): ConfigsState => {
  const [state, setState] = useState<ConfigsState>({
    configs: [],
    isLoading: true,
    failed: false,
  });

  useEffect(() => {
    let cancelled = false;
    setState((previous) => ({ ...previous, isLoading: true }));
    fetchSerenityConfigs(uuid)
      .then((configs) => {
        if (!cancelled) setState({ configs, isLoading: false, failed: false });
      })
      .catch(() => {
        if (!cancelled)
          setState({ configs: [], isLoading: false, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  return state;
};

const formatDate = (date: string) => new Date(date).toLocaleDateString();

/** Titre avec l'action « Comparer », la dernière sauvegarde en avant, les plus anciennes repliées. */
export const SerenityConfigsSection = ({ uuid }: { uuid: string }) => {
  const { t } = useTranslation();
  const { configs, isLoading, failed } = useSerenityConfigs(uuid);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [isCompareOpen, setIsCompareOpen] = useState(false);

  const title = (
    <TitleSection
      title={t("serenity-configs-title")}
      icon="download"
      onClick={configs.length >= 2 ? () => setIsCompareOpen(true) : undefined}
      actionIcon="code-compare"
      actionLabel={t("serenity-configs-compare")}
    />
  );

  if (isLoading) return <div className="text-sm">{title}…</div>;
  if (failed)
    return (
      <div className="text-sm">
        {title}
        {t("serenity-configs-error")}
      </div>
    );
  if (configs.length === 0) {
    return (
      <div className="text-sm">
        {title}
        {t("serenity-configs-none")}
      </div>
    );
  }

  const download = (id: number) => {
    setDownloadFailed(false);
    downloadSerenityConfig(uuid, id).catch(() => setDownloadFailed(true));
  };
  const [latest, ...older] = configs;

  return (
    <div className="flex flex-1 flex-col gap-2">
      {title}
      <button
        type="button"
        className="flex items-center gap-2 text-left text-sm font-oxaniumMedium text-tertiary hover:text-secondary"
        onClick={() => download(latest.id)}
      >
        <FontAwesomeIcon icon="download" className="h-3 w-3" />
        {t("serenity-configs-download", {
          date: formatDate(latest.date),
          version: latest.version,
        })}
      </button>
      {older.length > 0 && (
        <details className="group">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-xs text-grey-dark hover:text-tertiary">
            <FontAwesomeIcon
              icon="chevron-right"
              className="h-3 w-3 transition-transform group-open:rotate-90"
            />
            {t("serenity-configs-older", { count: older.length })}
          </summary>
          <div className="mt-2 flex flex-col gap-2 pl-5">
            {older.map((config) => (
              <button
                key={config.id}
                type="button"
                className="text-xs text-left text-grey-dark hover:text-tertiary"
                onClick={() => download(config.id)}
              >
                <FontAwesomeIcon icon="download" className="mr-2" />
                {t("serenity-configs-download", {
                  date: formatDate(config.date),
                  version: config.version,
                })}
              </button>
            ))}
          </div>
        </details>
      )}
      {downloadFailed && (
        <div className="text-xs text-warning-readable">
          {t("serenity-configs-download-error")}
        </div>
      )}
      {isCompareOpen && (
        <SerenityConfigCompareModal
          uuid={uuid}
          configs={configs}
          isOpen={isCompareOpen}
          onClose={() => setIsCompareOpen(false)}
        />
      )}
    </div>
  );
};
